/**
 * advance-minefield.ts — Minefield per-tick pass (v3M2.3b).
 *
 * Walks every Minefield-chassis ship and applies the deplete-on-contact
 * mechanic: hostile (or any, if IFF tech absent) ships within
 * `MINEFIELD_RADIUS` and not currently throttled trigger one mine. Each
 * trigger:
 *   - Deals `MINE_DAMAGE` to the ship's currentHull, after armor and
 *     bypassing shields (contact mines defeat the magnetic-shield
 *     model).
 *   - Decrements the minefield's `mineCount` by 1.
 *   - Stamps the current tick into the minefield's
 *     `mineLastTriggerTickByShipId` map so the same ship can't trigger
 *     another mine in this field for the next
 *     `MINE_TRIGGER_COOLDOWN_TICKS` ticks (10 = 1 second).
 *   - Sets `mineCloakRevealed = true` so a Cloaked-Mines minefield
 *     becomes visible after its first contact.
 *
 * Self Replicating Mines tech (`Minefields - Self Replicating Mines`):
 *   regenerates +1 to `mineCount` per tick up to `MINE_COUNT_CAP`,
 *   regardless of whether the field triggered this tick.
 *
 * IFF tech (`Minefields - IFF`): only hostile ships (different empire
 * AND different team) trigger mines. Without it, friendly ships
 * trigger too — friendly fire is the v1 default.
 *
 * Cloaked Mines tech is read by `advance-detection.ts` to suppress
 * teamVision additions until `mineCloakRevealed` flips; the trigger
 * mechanic itself fires regardless of cloak state (a ship driving
 * into invisible mines still gets blown up).
 *
 * End-of-pass cleanup despawns minefields whose `mineCount` reaches 0
 * (depleted) and ships whose hull dropped to ≤0 (mirror of the combat
 * pass cull pattern). Determinism: minefields walked in id order;
 * candidate ships walked in id order.
 */

import {
  MINEFIELDS_IFF_TECH,
  MINEFIELDS_SELF_REPLICATING_TECH,
  MINEFIELD_CHASSIS,
  mineCountCap,
  mineDamage,
  mineTriggerCooldownTicks,
  minefieldRadius,
} from './balance-space-structures';
import { effectiveArmor } from './research-effects';
import type { GameState, ShipInstance, StructuresData } from '../state/types';

export function advanceMinefield(state: GameState): void {
  const tick = state.tick;
  const sd = state.structuresData;
  const cap = mineCountCap(sd);
  const radius = minefieldRadius(sd);
  const radiusSq = radius * radius;
  const cooldown = mineTriggerCooldownTicks(sd);
  // Iterate minefields in id order for determinism.
  const fields: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.chassis === MINEFIELD_CHASSIS) fields.push(ship);
  }
  fields.sort((a, b) => a.id - b.id);

  for (const field of fields) {
    if (field.systemId === null) continue; // mid-jump (defensive — shouldn't happen)
    if (field.mineCount === undefined) continue; // safety — uninitialised field
    const owner = state.empires.get(field.ownerEmpireId);
    if (!owner) continue;

    const iffActive = owner.researchedTechs.has(MINEFIELDS_IFF_TECH);
    const selfReplicating = owner.researchedTechs.has(
      MINEFIELDS_SELF_REPLICATING_TECH,
    );

    // Self Replicating Mines — regen up to cap. Runs before triggers
    // so a freshly-replenished mine is available this tick.
    if (selfReplicating && field.mineCount < cap) {
      field.mineCount = Math.min(cap, field.mineCount + 1);
    }

    if (field.mineCount <= 0) continue;

    // Find candidate ships in radius. Ships are walked in id order so
    // ties (multiple ships in radius this tick) trigger lowest-id-first.
    const candidates: ShipInstance[] = [];
    for (const ship of state.ships.values()) {
      if (ship.id === field.id) continue;
      if (ship.systemId !== field.systemId) continue;
      if (ship.currentHull <= 0) continue; // already dead
      if (iffActive && ship.ownerEmpireId === owner.id) continue; // IFF: skip friendlies
      const dx = ship.sx - field.sx;
      const dy = ship.sy - field.sy;
      if (dx * dx + dy * dy > radiusSq) continue;
      candidates.push(ship);
    }
    candidates.sort((a, b) => a.id - b.id);

    // Trigger mines, respecting per-target throttle and remaining mine
    // count. Stop early once mines run out.
    if (!field.mineLastTriggerTickByShipId) {
      field.mineLastTriggerTickByShipId = [];
    }
    const throttleMap = new Map<number, number>(field.mineLastTriggerTickByShipId);
    for (const ship of candidates) {
      if (field.mineCount <= 0) break;
      const lastTick = throttleMap.get(ship.id);
      if (lastTick !== undefined && tick - lastTick < cooldown) {
        continue; // still cooling down for this target
      }
      // Trigger.
      applyMineDamage(sd, state, ship);
      field.mineCount -= 1;
      field.mineCloakRevealed = true;
      throttleMap.set(ship.id, tick);
    }
    // Persist the throttle map back into the JSON-safe entry array,
    // sorted by ship-id ascending for deterministic save round-trip.
    field.mineLastTriggerTickByShipId = [...throttleMap.entries()].sort(
      (a, b) => a[0] - b[0],
    );
  }

  // Cull depleted minefields (mineCount === 0) and dead ships. Mirror
  // of the advance-combat / advance-surface-batteries despawn pattern.
  const dead: number[] = [];
  for (const ship of state.ships.values()) {
    if (
      ship.chassis === MINEFIELD_CHASSIS &&
      ship.mineCount !== undefined &&
      ship.mineCount <= 0
    ) {
      dead.push(ship.id);
      continue;
    }
    if (ship.currentHull <= 0) dead.push(ship.id);
  }
  if (dead.length === 0) return;
  dead.sort((a, b) => a - b);
  for (const id of dead) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    state.ships.delete(id);
    const empire = state.empires.get(ship.ownerEmpireId);
    if (empire) {
      const idx = empire.shipIds.indexOf(id);
      if (idx >= 0) empire.shipIds.splice(idx, 1);
    }
    for (const inner of state.teamVision.values()) {
      inner.delete(id);
    }
  }
}

/**
 * Mine damage application: armor reduces, shields are bypassed
 * (contact mines defeat magnetic shielding). 200 damage × (1 - armor)
 * goes straight to hull.
 */
function applyMineDamage(
  sd: StructuresData,
  state: GameState,
  target: ShipInstance,
): void {
  const empire = state.empires.get(target.ownerEmpireId);
  const tmpl =
    empire !== undefined
      ? state.shipTemplates.get(`${empire.raceId}:${target.chassis}`)
      : undefined;
  const armorFraction =
    tmpl !== undefined && empire !== undefined
      ? Math.max(0, Math.min(1, effectiveArmor(state, empire, tmpl)))
      : 0;
  const hullDamage = mineDamage(sd) * (1 - armorFraction);
  target.currentHull = Math.max(0, target.currentHull - hullDamage);
}
